// Goods in: what the two goods-in pages share — the payload shapes of
// GET /receipts and GET /receipts/{id}, and the words they use for states and
// flags. Kept out of the pages so the register and the detail can never label
// the same state two ways.
//
// The API computes every state and flag (api/src/domain/cofaRules.ts). Nothing
// here re-derives one; it only puts them into words.

import type { CofaResultVerdict, CofaVerdict } from '../../components/VerdictBadge';

/* ⚠ The five types below mirror CHECK constraints in migration 012, and
 * api/src/__tests__/vocabulary.test.ts reads them against the database (rule
 * 14). Each is rendered through a Record, so a value added to the database and
 * not here is a type error, not a silent fall-through to a default label. */
export type CofaDecision = 'APPROVED' | 'REJECTED' | 'NEW_COFA_REQUESTED';
export type SourceKind = 'EMAIL' | 'HARD_COPY' | 'LAB_REPORT';
export type ReadMode = 'EXTRACTED' | 'MANUAL_READ';
export type CertificateType = 'COA' | 'COC';
export type CofaArrives = 'EMAIL' | 'ON_REQUEST' | 'HARD_COPY';

/* Identity and signature (migration 016), mirrored the same way (rule 14). */
export type CofaFieldKind = 'ISSUER' | 'PRODUCT' | 'SIGNATURE';
export type NameMatch = 'EXACT' | 'ALIAS' | 'NEAR' | 'NONE';
export type SignatureKind = 'PRINTED_NAME' | 'ELECTRONIC_STATEMENT' | 'IMAGE' | 'NONE';
export type IssuerKind = 'MANUFACTURER' | 'LABORATORY' | 'TRADER';
export type AttestationKind = 'IDENTITY' | 'SIGNATURE';
/** What the CofA intake job did with a QA@ attachment (migration 018, rule 25). */
export type IntakeOutcome =
  | 'RECORDED'
  | 'NOT_A_CERTIFICATE'
  | 'UNREADABLE'
  | 'UNMATCHED'
  | 'WAITING_RECEIPT'
  | 'DUPLICATE'
  | 'FAILED'
  | 'DRYRUN'
  | 'SAME_FILE_SECOND_DELIVERY'
  | 'STOPPED_RETRYING'
  | 'NEEDS_PERSON'
  | 'ON_PAPER';

export type CofaState =
  | 'NOT_FOUND'
  | 'AWAITING_DECISION'
  | 'APPROVED'
  | 'REJECTED'
  | 'NEW_COFA_REQUESTED'
  | 'BATCHES_UNKNOWN'
  /** Every batch was received before the cutover: the paper system handled it. */
  | 'ON_PAPER';

export type RegisterFlag =
  | 'BATCH_READ_FAILED'
  | 'RECEIVED_NOT_APPROVED'
  | 'BATCH_OUTSIDE_APPROVAL'
  | 'BATCH_NOT_EXACT'
  | 'NO_BATCH_IN_D365'
  | 'REQUEST_DUE'
  | 'REMINDER_DUE'
  | 'DELIVERY_OVERDUE';

export type LotMatch = 'EXACT' | 'NEAR' | 'NONE';

export interface RegisterRow {
  receiptId: number;
  po: string;
  line: number;
  item: string;
  itemName: string | null;
  category: string | null;
  /** onHold: D365's OnHoldStatus ('No' = not held); null or absent = not in the vendor snapshot (039). */
  vendor: { account: string | null; name: string | null; onHold?: string | null };
  expectedDate: string | null;
  lineStatus: string | null;
  receivedQty: number | null;
  /** The unit receivedQty is in (Receipt.StockUnit, migration 028). Null or absent: not known. */
  stockUnit?: string | null;
  /** Null when D365 could not be read: unknown, not "no" (rule 15). */
  received: boolean | null;
  batches: Array<{
    batchId: string;
    packingSlipId: string | null;
    qty: number | null;
    receivedDate: string | null;
    bestBeforeDate: string | null;
    /** Null when D365 could not be read. */
    approved: boolean | null;
    /** Received before the cutover with nothing about it here: the paper system
     *  handled it. Not an approval. Absent from an API older than the cutover. */
    onPaper?: boolean | null;
  }>;
  batchReadError: string | null;
  state: CofaState;
  flags: RegisterFlag[];
  unapprovedBatchIds: string[] | null;
  requestDueOn: string | null;
  /** Undecided live certificates that ask for no decision: every batch they name was approved, with
   *  it in view, after they were recorded (one decision per received batch, Amit 24 Oct 2026). The
   *  server's answer (registerState); absent from an older API, which asked for every one. */
  noDecisionNeeded?: Array<{ cofaId: number; approvedAt: string; approvedCofaId: number }>;
  certificate: {
    cofaId: number;
    recordedAt: string | null;
    recordedByUpn: string;
    foundBy: string;
    readMode: ReadMode;
    certificateType: CertificateType;
    lots: string[];
    /** Null when D365 could not be read for the line. */
    batchesExact: boolean | null;
    /** How it was judged when recorded; null if recorded before verdicts (migration 014). */
    verdict?: CofaVerdict | null;
    /** `automatic`: the intake job's safe auto-approve (migration 030): results only, identity and
     *  signature not checked. Absent from an API older than it: read as a person's. */
    decision: { decision: CofaDecision; decidedByUpn: string; decidedAt: string | null; automatic?: boolean } | null;
  } | null;
  certificateCount: number;
  liveCertificateCount: number;
  chases: { count: number; last: { kind: string; loggedAt: string | null; loggedByUpn: string } | null };
  supplierRule: {
    vendorAccount: string | null;
    cofaArrives: CofaArrives;
    matchKey: string;
    requestLeadDays: number | null;
    senderDomains: string | null;
    notes: string | null;
    isDefault: boolean;
    /** Suppliers settings (migrations 036, 038, 039). Absent from an older API. */
    contactEmail?: string | null;
    lotFormat?: 'EXACT' | 'DATE_CODE_PALLET' | 'REFERENCE_SUFFIX';
    autoApprove?: boolean;
  };
  /** The dock's incoming inspection (migration 017). NONE = nothing recorded (read fine);
   *  null or absent = not known (an API older than 017). Information, never a gate. */
  iir?: IirSummary | null;
  syncedAt: string | null;
}

/* ── The incoming inspection at the dock (IIR, migration 017) ── */

export type IirStatus = 'NONE' | 'PASS' | 'FAIL';

export interface IirSummary {
  status: IirStatus;
  inspectedAt: string | null;
  by: string | null;
  count: number;
  /** FAIL only because a version since corrected failed ("Failed, corrected"). A FAIL is never erased. */
  corrected?: boolean;
  /** A live vehicle inspection names this line, it passed or its failure was closed with the load accepted, and
   *  no goods inspection covers the line yet (migration 033; per line since the audit of 28 Oct 2026).
   *  Absent = an API older than 033: not known, never "not pending" (rule 15). */
  goodsPending?: boolean;
}

/** The register's Inspection cell (migration 033): a vehicle that passed with its goods still to
 *  inspect is QA's next step, so it reads as that - unless a record failed, which is never hidden. */
export function iirRegisterWord(s: IirSummary): { text: string; tone: Tone } {
  if (s.goodsPending && s.status !== 'FAIL') return { text: 'Vehicle passed, goods not inspected', tone: 'warn' };
  // A vehicle failure QA closed with the load accepted leaves its goods waiting too (audit
  // 2026-10-28): the FAIL is still said first, never hidden behind the next step.
  if (s.goodsPending) return { text: `${iirStatusLabel(s)}; goods not inspected`, tone: IIR_TONE[s.status] };
  return { text: iirStatusLabel(s), tone: IIR_TONE[s.status] };
}

/** The stages of a delivery's inspection (migration 033, Amit 27 Oct 2026): the warehouse records the
 *  VEHICLE inspection, QA the GOODS inspection after it. FULL = a record made before, both halves. */
export type InspectionStage = 'FULL' | 'VEHICLE' | 'GOODS';

/** A VEHICLE record's PO lines on the truck. */
export interface InspectionLine {
  receiptId: number;
  po: string;
  line: number;
  item: string;
  itemName: string | null;
  appUrl?: string;
}

/** On a GOODS record: the vehicle inspection it follows, read-only. */
export interface VehicleInspectionRef {
  inspectionId: number;
  result: 'PASS' | 'FAIL';
  inspectedByUpn: string;
  recordedAt: string | null;
  deliveryCompany: string | null;
  vehicleRego: string | null;
  comments: string | null;
  checks: IirCheck[];
}

export type IirCofaMatchKind = 'EXACT' | 'NEAR' | 'NONE' | 'NO_CERTIFICATE' | 'NO_LOT';

export interface IirCheck {
  check: string;
  label: string;
  /** Null = NOT ASKED: the check joined the form (23 Oct 2026, migration 022) after the record was
   *  made. Neither a pass nor a fail (rule 15). */
  result: 'PASS' | 'FAIL' | null;
  reason: string | null;
}

/** The checks a record was never asked (null), by label: "" when it answered them all. */
export function notAskedText(checks: readonly IirCheck[]): string {
  const labels = checks.filter((c) => c.result === null).map((c) => c.label);
  return labels.length ? `Not asked (added to the form after this record): ${labels.join('; ')}.` : '';
}

export interface Inspection {
  inspectionId: number;
  /** Migration 033. Absent = an API older than 033: read as a FULL record, as before. */
  stage?: InspectionStage;
  /** GOODS: the vehicle inspection it follows. Null otherwise. */
  vehicleInspectionId?: number | null;
  /** VEHICLE: the PO lines on the truck (it has no batch rows). Absent = not known. */
  lines?: InspectionLine[];
  /** GOODS: the vehicle inspection it follows, summarised. Null when not a GOODS record. */
  vehicleInspection?: VehicleInspectionRef | null;
  /** VEHICLE: the live goods inspection after it; null = QA has not inspected the goods yet;
   *  absent = not known (rule 15). */
  goodsInspectionId?: number | null;
  supersedes: number | null;
  supersededBy: number | null;
  vendor: { account: string | null; name: string | null };
  deliveryCompany: string | null;
  vehicleRego: string | null;
  dateReceived: string | null;
  vehicle: IirCheck[];
  vehicleFailed: boolean;
  batches: Array<{
    receiptId: number;
    sequence: number;
    po: string;
    line: number;
    item: string;
    itemName: string | null;
    batchId: string | null;
    lotAsPrinted: string | null;
    manufacturedDate: string | null;
    bestBeforeDate: string | null;
    quantityKg: number | null;
    checks: IirCheck[];
    comment: string | null;
    failed: boolean;
    cofaMatch: {
      kind: IirCofaMatchKind;
      cofaId: number | null;
      lot: string | null;
      reason: string | null;
      note: string | null;
      /** Where the latest decision about the BATCH stands - the register's own answer (rule 18).
       *  Null with no certificate. */
      standing: IirStanding | null;
      /** The latest decision about the batch is an approval covering it. */
      covered: boolean;
    };
  }>;
  comments: string | null;
  result: 'PASS' | 'FAIL';
  inspectedByUpn: string;
  capturedAtDevice: string | null;
  recordedAt: string | null;
  door: string;
  /** The inspection failure this record's lineage is (migration 020): OPEN until QA closes it, CLOSED
   *  with who, when and why; null when no version ever failed. ABSENT (undefined) is an API that did
   *  not say - unknown, never "no failure" (rule 15). */
  failure?: InspectionFailureRef | null;
  /** A correction's author (QA or the inspector) and why it was made; null on an original. */
  correctedByUpn?: string | null;
  correctionNote?: string | null;
  /** A version this one replaced failed: "Failed, corrected". */
  lineageFailed?: boolean;
  /** Photos taken for this record (migration 019), metadata only; the bytes come from
   *  GET /receipt-inspections/{id}/photos/{photoId}. Optional: an older API sends none. */
  photos?: InspectionPhoto[];
}

export interface InspectionPhoto {
  photoId: number;
  /** The row's sequence on the inspection, or null for the delivery. */
  batchSequence: number | null;
  checkCode: string | null;
  contentType: string;
  bytes: number;
  uploadedByUpn: string;
  capturedAtDevice: string | null;
  recordedAt: string | null;
}

/**
 * Every photo of a live record AND of each version it corrected, back to the original - each
 * labelled with the inspection it was taken for (review, 22 Oct 2026: a correction carries no
 * photos, so showing only the live record's hid the dock's pictures the moment QA corrected it).
 * `known` is false when any version's photos were not sent, or a version in the chain is not in
 * `all`: unknown, never "no photos" (rule 15).
 */
export function lineagePhotos(
  live: Inspection,
  all: readonly Inspection[],
): { known: boolean; photos: Array<{ fromInspectionId: number; photo: InspectionPhoto; checks: IirCheck[] }> } {
  const byId = new Map(all.map((i) => [i.inspectionId, i]));
  const photos: Array<{ fromInspectionId: number; photo: InspectionPhoto; checks: IirCheck[] }> = [];
  let known = true;
  const seen = new Set<number>();
  for (let v: Inspection | undefined = live; v && !seen.has(v.inspectionId); ) {
    seen.add(v.inspectionId);
    if (v.photos === undefined) known = false;
    const checks = [...v.vehicle, ...(v.batches[0]?.checks ?? [])];
    for (const photo of v.photos ?? []) photos.push({ fromInspectionId: v.inspectionId, photo, checks });
    if (v.supersedes === null) break;
    const prev = byId.get(v.supersedes);
    if (!prev) known = false;
    v = prev;
  }
  return { known, photos };
}

/** What a photo is of, in words: "Delivery", "Vehicle: Clean interior", "Row 2: Odour". */
export function photoCaption(p: Pick<InspectionPhoto, 'batchSequence' | 'checkCode'>, checks: ReadonlyArray<{ check: string; label: string }>): string {
  const label = p.checkCode ? (checks.find((c) => c.check === p.checkCode)?.label ?? p.checkCode) : null;
  if (p.batchSequence === null) return label ? `Vehicle: ${label}` : 'Delivery';
  return label ? `Row ${p.batchSequence}: ${label}` : `Row ${p.batchSequence}`;
}

export type IirStanding = 'PENDING' | CofaDecision | 'NONE';

const STANDING_TEXT: Record<IirStanding, string> = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  NEW_COFA_REQUESTED: 'new certificate requested',
  PENDING: 'awaiting a decision',
  NONE: 'no decision',
};

/** A record's stage; an API older than 033 sends none, and every record then was FULL. */
export function stageOf(i: Pick<Inspection, 'stage'>): InspectionStage {
  return i.stage ?? 'FULL';
}

export const STAGE_LABEL: Record<InspectionStage, string> = { FULL: 'Inspection', VEHICLE: 'Vehicle inspection', GOODS: 'Goods inspection' };

/**
 * The live records in the handoff's order (migration 033): each vehicle inspection followed by the
 * goods inspection after it; anything else where the server put it (newest first).
 */
export function handoffOrder(live: readonly Inspection[]): Inspection[] {
  const vehicles = new Set(live.filter((i) => stageOf(i) === 'VEHICLE').map((i) => i.inspectionId));
  const out: Inspection[] = [];
  for (const i of live) {
    if (stageOf(i) === 'GOODS' && i.vehicleInspectionId != null && vehicles.has(i.vehicleInspectionId)) continue;
    out.push(i);
    if (stageOf(i) === 'VEHICLE') out.push(...live.filter((g) => stageOf(g) === 'GOODS' && g.vehicleInspectionId === i.inspectionId));
  }
  return out;
}

/** Where the goods stand after a vehicle inspection, in words; null when a goods record follows it. */
export function goodsAfterVehicle(v: Inspection): { text: string; tone: Tone } | null {
  if (v.goodsInspectionId === undefined) return { text: 'Goods inspection: not known - the server did not say.', tone: 'quiet' };
  if (v.goodsInspectionId !== null) return null;
  if (v.result === 'FAIL') {
    const f = v.failure;
    if (f && f.status === 'CLOSED' && f.closure?.outcome === 'REJECTED') return { text: 'Load rejected: there are no goods to inspect.', tone: 'quiet' };
    if (!(f && f.status === 'CLOSED' && (f.closure?.outcome === 'ACCEPTED' || f.closure?.outcome === 'ACCEPTED_WITH_CONDITIONS'))) {
      return { text: 'Goods stay on the truck until QA closes the failure.', tone: 'bad' };
    }
  }
  return { text: 'Goods not inspected yet: QA inspects them on the tablet (Receiving).', tone: 'warn' };
}

/** The register's word, as shown: a FAIL a correction replaced still reads as one. */
export function iirStatusLabel(s: IirSummary): string {
  return s.status === 'FAIL' && s.corrected ? 'Failed, corrected' : IIR_LABEL[s.status];
}

/**
 * The body of a correction (POST /receipt-inspections with supersedesId), from the record shown
 * and the checks a person changed. Everything else is copied, so the correction covers every PO
 * line the original did, as the server requires. `edits` is keyed `V:<check>` for the truck and
 * `<sequence>:<check>` for a row.
 */
export function correctionPayload(
  i: Inspection,
  edits: Record<string, { result: 'PASS' | 'FAIL' | null; reason: string }>,
  note: string,
  clientInspectionId: string,
): Record<string, unknown> {
  const answer = (key: string, c: IirCheck) => {
    const e = edits[key];
    const result = e?.result ?? c.result;
    const reason = (e ? e.reason : (c.reason ?? '')).trim();
    return { result, ...(reason ? { reason } : {}) };
  };
  const head = { clientInspectionId, supersedesId: i.inspectionId, correctionNote: note.trim() };
  const vehicle = () => Object.fromEntries(i.vehicle.map((c) => [c.check, answer(`V:${c.check}`, c)]));
  const batches = () =>
    i.batches.map((b) => ({
      receiptId: b.receiptId,
      batchId: b.batchId,
      lotAsPrinted: b.lotAsPrinted,
      manufacturedDate: b.manufacturedDate,
      bestBeforeDate: b.bestBeforeDate,
      quantityKg: b.quantityKg,
      comment: b.comment,
      checks: Object.fromEntries(b.checks.map((c) => [c.check, answer(`${b.sequence}:${c.check}`, c)])),
    }));
  // A correction keeps the stage of the record it corrects (migration 033; the server refuses a
  // mismatch as correction_mismatch). VEHICLE: the truck and its PO lines, no batches. GOODS: the
  // batches and the vehicle record it follows; the truck's date, carrier and rego are that record's.
  if (i.stage === 'VEHICLE') {
    return {
      ...head,
      stage: 'VEHICLE',
      dateReceived: i.dateReceived,
      deliveryCompany: i.deliveryCompany,
      vehicleRego: i.vehicleRego,
      comments: i.comments,
      vehicle: vehicle(),
      receiptIds: (i.lines ?? []).map((l) => l.receiptId),
    };
  }
  if (i.stage === 'GOODS') {
    return { ...head, stage: 'GOODS', vehicleInspectionId: i.vehicleInspectionId ?? null, comments: i.comments, batches: batches() };
  }
  return {
    ...head,
    ...(i.stage === 'FULL' ? { stage: 'FULL' } : {}),
    dateReceived: i.dateReceived,
    deliveryCompany: i.deliveryCompany,
    vehicleRego: i.vehicleRego,
    comments: i.comments,
    vehicle: vehicle(),
    batches: batches(),
  };
}

export const IIR_LABEL: Record<IirStatus, string> = { NONE: 'Not inspected', PASS: 'Passed', FAIL: 'Failed' };
export const IIR_TONE: Record<IirStatus, Tone> = { NONE: 'quiet', PASS: 'ok', FAIL: 'bad' };

export const IIR_MATCH_LABEL: Record<IirCofaMatchKind, string> = {
  EXACT: 'Match',
  NEAR: 'Near match',
  NONE: 'No match',
  NO_CERTIFICATE: 'No certificate yet',
  NO_LOT: 'No lot written',
};
export const IIR_MATCH_TONE: Record<IirCofaMatchKind, Tone> = {
  EXACT: 'ok',
  NEAR: 'warn',
  NONE: 'bad',
  NO_CERTIFICATE: 'quiet',
  NO_LOT: 'quiet',
};

export interface RegisterPayload {
  today: string;
  window: { from: string; to: string } | null;
  po: string | null;
  windowSyncedAt: string | null;
  lastSyncedAt: string | null;
  count: number;
  byState: Record<string, number>;
  byFlag: Record<string, number>;
  warnings: string[];
  /** Open inspection failures across goods in (migration 020). Absent = not known. */
  inspectionFailures?: { open: number };
  rows: RegisterRow[];
}

export interface Coverage {
  received: boolean;
  exact: boolean;
  batches: Array<{
    batchId: string;
    packingSlipId?: string | null;
    match: LotMatch;
    lot: string | null;
    reason: string | null;
    note: string | null;
    /** Received before the cutover and not named by this certificate: not in
     *  front of a decision on it (evaluateCoverage, api/src/domain/cofaRules.ts). */
    onPaper?: boolean;
  }>;
  lots: Array<{ lot: string; match: LotMatch; batchId: string | null; reason: string | null }>;
}

export interface Certificate {
  cofaId: number;
  supersedes: number | null;
  supersededBy: number | null;
  sourceKind: SourceKind;
  certificateType: CertificateType;
  readMode: ReadMode;
  foundBy: string;
  source: {
    mailbox: string | null;
    messageId: string | null;
    subject: string | null;
    senderAddress: string | null;
    emailReceivedAt: string | null;
    attachmentName: string | null;
    /** The file the server read and kept (rule 22): both null when it kept none. The viewer
     *  (CertificateFile.tsx) offers the file only when sha256 is set. */
    sha256: string | null;
    blobUri: string | null;
  };
  issuer: string | null;
  certificateNumber: string | null;
  issuedDate: string | null;
  notes: string | null;
  recordedByUpn: string;
  recordedAt: string | null;
  /** The door it came through: SPA, GATEWAY (Claude), or SYSTEM - the intake job (rule 25). */
  door: string;
  lots: Array<{ lot: string; quote: string | null; page: number | null; manufacturedDate: string | null; bestBeforeDate: string | null }>;
  results: Array<{
    sequence: number;
    parameter: string;
    result: string;
    value: number | null;
    unit: string | null;
    printedLimit: string | null;
    quote: string | null;
    page: number | null;
    verdict?: CofaResultVerdict | null;
    verdictReason?: string | null;
    /** The spec check the server paired this result to; null = not in the spec. */
    specParameterId?: number | null;
  }>;
  /** Against the supplier's spec as it stood when recorded (rule 6). Null if recorded
   *  before verdicts existed. A recommendation: the decision below is a person's. */
  verdict?: CofaVerdict | null;
  /** As stored when recorded: the material and spec version judged against, and every reason it is not PASS. */
  verdictReasons?: { reasons?: string[]; spec?: { status?: string } | null } | null;
  /** The spec version it was judged against (rule 6). */
  specVersionId?: number | null;
  /** Every line of that version (COFA checks), for the spec table (29 Oct 2026). Empty: no spec. */
  specLines?: SpecLine[];
  /** QA's newest note per line (migration 036). */
  comments?: Array<{ lineKey: string; comment: string; byUpn: string; at: string | null }>;
  /** Mandatory checks the certificate does not report. */
  missing?: Array<{ parameter: string; specParameterId: number | null }>;
  /** The certificate whose FAIL approving this one stands on — its own, one it corrects, or
   *  another live one for the same goods (failStandingOn, the API's answer). */
  failedOn?: number | null;
  /** Null when D365 could not be read for the line: no match is claimed. */
  coverage: Coverage | null;
  /** What the server computed when it recorded this row (016); null on rows recorded before. */
  proven?: { identity: boolean; signed: boolean } | null;
  /** The issuer, the product and the signature as printed, each with its quote (016). */
  fields?: Array<{
    field: CofaFieldKind;
    asPrinted: string | null;
    kind: SignatureKind | null;
    role: string | null;
    quote: string | null;
    page: number | null;
    /** True: found visibly in the file. Null: not shown to be (never false — rule 15). */
    quoteVerified: boolean | null;
    match: NameMatch | null;
    matchNote: string | null;
  }>;
  /** A person standing behind this record; resultingCofaId is the record it produced. */
  attestations?: Array<{
    attestationId: number;
    kind: AttestationKind;
    note: string;
    attestedByUpn: string;
    attestedAt: string | null;
    resultingCofaId: number;
  }>;
  decisions: Array<{
    decisionId: number;
    decision: CofaDecision;
    note: string | null;
    ncrId: string | null;
    decidedByUpn: string;
    decidedAt: string | null;
    /** The intake job's safe auto-approve (migration 030). */
    automatic?: boolean;
  }>;
}

export interface ReceiptDetail extends Omit<RegisterRow, 'batches'> {
  batches: Array<{
    batchId: string;
    packingSlipId: string | null;
    qty: number | null;
    receivedDate: string | null;
    manufacturedDate: string | null;
    bestBeforeDate: string | null;
    disposition: string | null;
    approved: boolean | null;
    onPaper?: boolean | null;
  }>;
  /** The day the register took over from paper (YYYY-MM-DD). */
  cutover?: string;
  orderedQty: number | null;
  unit: string | null;
  warehouse: string | null;
  certificates: Certificate[];
  chaseHistory: Array<{
    kind: string;
    sentTo: string | null;
    draftMessageId: string | null;
    note: string | null;
    loggedByUpn: string;
    loggedAt: string | null;
  }>;
  nearMatchHelp: Record<string, string | null>;
  /** Issuers a person approved for this vendor, for this item or any item (016). */
  approvedIssuers?: Array<{
    issuerId: number;
    vendorAccount: string;
    d365ItemId: string | null;
    issuerName: string;
    kind: IssuerKind;
    evidenceCofaId: number;
    approvedByUpn: string;
    approvedAt: string | null;
  }>;
  /** The material this line's certificates are judged against, and its approved names. */
  material?: {
    materialId: number;
    name: string;
    aliases: Array<{ aliasId: number; alias: string; evidenceCofaId: number; approvedByUpn: string; approvedAt: string | null }>;
  } | null;
  /** Every incoming inspection of a delivery carrying this line, newest first (017).
   *  Absent = an API older than 017, which is not "none recorded". */
  inspections?: Inspection[];
}

export const FIELD_LABEL: Record<CofaFieldKind, string> = {
  ISSUER: 'Issued by',
  PRODUCT: 'Product',
  SIGNATURE: 'Signed',
};

export const NAME_MATCH_LABEL: Record<NameMatch, string> = {
  EXACT: 'On the order',
  ALIAS: 'Approved name',
  NEAR: 'Near',
  NONE: 'Not on the order',
};

export const NAME_MATCH_TONE: Record<NameMatch, Tone> = { EXACT: 'ok', ALIAS: 'ok', NEAR: 'warn', NONE: 'bad' };

export const SIGNATURE_LABEL: Record<SignatureKind, string> = {
  PRINTED_NAME: 'a printed name',
  ELECTRONIC_STATEMENT: 'an electronic-signature statement',
  IMAGE: 'an image only (a person attests it)',
  NONE: 'nothing authorises it',
};

export const ISSUER_KIND_LABEL: Record<IssuerKind, string> = {
  MANUFACTURER: 'Manufacturer',
  LABORATORY: 'Laboratory',
  TRADER: 'Trader',
};

export const ATTESTATION_LABEL: Record<AttestationKind, string> = {
  IDENTITY: 'identity',
  SIGNATURE: 'signature',
};

/** Everyone who recorded any version of a certificate, following `supersedes` through the line's
 *  certificates. The server refuses each of them an attestation or an approval from it (rule 9's
 *  shape); the screen hides the buttons for them so nobody is offered a refusal. */
export function recordersOf(certificates: readonly Pick<Certificate, 'cofaId' | 'supersedes' | 'recordedByUpn'>[], cofaId: number): Set<string> {
  const out = new Set<string>();
  const byId = new Map(certificates.map((c) => [c.cofaId, c]));
  for (let c = byId.get(cofaId); c && !out.has(`#${c.cofaId}`); c = c.supersedes ? byId.get(c.supersedes) : undefined) {
    out.add(`#${c.cofaId}`);
    out.add(c.recordedByUpn.toLowerCase());
  }
  return new Set([...out].filter((x) => !x.startsWith('#')));
}

export const STATE_LABEL: Record<CofaState, string> = {
  NOT_FOUND: 'Not found',
  AWAITING_DECISION: 'Awaiting decision',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  NEW_COFA_REQUESTED: 'New certificate requested',
  BATCHES_UNKNOWN: 'Batches unknown',
  ON_PAPER: 'On paper',
};

export const FLAG_LABEL: Record<RegisterFlag, string> = {
  BATCH_READ_FAILED: 'Batches could not be read from D365',
  RECEIVED_NOT_APPROVED: 'Received, not approved',
  BATCH_OUTSIDE_APPROVAL: 'Batch outside the approval',
  BATCH_NOT_EXACT: 'Batch not named exactly',
  NO_BATCH_IN_D365: 'Received with no batch in D365',
  REQUEST_DUE: 'Request due',
  REMINDER_DUE: 'Reminder due',
  DELIVERY_OVERDUE: 'Delivery overdue',
};

export const DECISION_LABEL: Record<CofaDecision, string> = {
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  NEW_COFA_REQUESTED: 'New certificate requested',
};

export const SOURCE_LABEL: Record<SourceKind, string> = {
  EMAIL: 'Email',
  HARD_COPY: 'Hard copy received with the goods',
  LAB_REPORT: 'Our own laboratory report',
};

export const READ_MODE_LABEL: Record<ReadMode, string> = {
  EXTRACTED: "Read from the file's text",
  MANUAL_READ: 'Read by hand from a file the tools could not read',
};

export const CERTIFICATE_TYPE_LABEL: Record<CertificateType, string> = {
  COA: 'certificate of analysis',
  COC: 'certificate of conformance (no results)',
};

export const ARRIVES_LABEL: Record<CofaArrives, string> = {
  EMAIL: 'by email, unasked',
  ON_REQUEST: 'only when asked',
  HARD_COPY: 'as a hard copy with the goods',
};

/** Three tones, from the theme's own triads. Red is spent only where goods are
 *  on site without cover, or a certificate was turned down. */
export type Tone = 'bad' | 'warn' | 'quiet' | 'ok';

export const FLAG_TONE: Record<RegisterFlag, Tone> = {
  BATCH_READ_FAILED: 'bad',
  RECEIVED_NOT_APPROVED: 'bad',
  BATCH_OUTSIDE_APPROVAL: 'warn',
  BATCH_NOT_EXACT: 'warn',
  NO_BATCH_IN_D365: 'warn',
  REQUEST_DUE: 'warn',
  REMINDER_DUE: 'warn',
  DELIVERY_OVERDUE: 'quiet',
};

export const STATE_TONE: Record<CofaState, Tone> = {
  NOT_FOUND: 'quiet',
  AWAITING_DECISION: 'warn',
  APPROVED: 'ok',
  REJECTED: 'bad',
  NEW_COFA_REQUESTED: 'warn',
  BATCHES_UNKNOWN: 'bad',
  ON_PAPER: 'quiet',
};

/** The same tones as text alone, for a word inside a cell. */
export const TONE_TEXT: Record<Tone, string> = {
  bad: 'text-(--bad)',
  warn: 'text-(--warn)',
  quiet: 'text-(--muted)',
  ok: 'text-(--in-fg)',
};

export const TONE_CLASS: Record<Tone, string> = {
  bad: 'border-(--bad-line) bg-(--bad-bg) text-(--bad)',
  warn: 'border-(--warn-line) bg-(--warn-bg) text-(--warn)',
  quiet: 'border-(--border) text-(--muted)',
  ok: 'border-(--ok-line) bg-(--subtle) text-(--fg)',
};

/** How an automatic approval is said wherever a decision is shown (Amit, 24 Oct 2026). */
export const AUTOMATIC_APPROVAL = 'Approved automatically';
export const AUTOMATIC_APPROVAL_WHY = 'by the intake job: results only - identity and signature not checked';

export const chipClass =
  'inline-flex items-center rounded-(--radius-pill) border px-(--s2) py-[2px] text-(length:--fs-sm) font-semibold';

/** YYYY-MM-DD for a local calendar day, `offset` days from today. */
export function localDay(offset = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "2 Oct" for a YYYY-MM-DD, or a dash. Dates here are calendar days, so they
 *  are formatted as such rather than shifted through a timezone. */
export function shortDay(day: string | null | undefined): string {
  if (!day) return '—';
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2 Sep" this year, "5 Feb 2027" any other — or always with the year. A
 *  made / best-before pair without years read "made Apr 29, best before Apr 28",
 *  which looks expired and is a year of shelf life (2026-10-01). */
export function dayLabel(day: string | null | undefined, alwaysYear = false): string {
  if (!day) return '—';
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return day;
  const year = !alwaysYear && y === new Date().getFullYear() ? '' : ` ${y}`;
  return `${d} ${MONTHS[m - 1]}${year}`;
}

/** The register's order (Amit, 24 Oct 2026): lines whose batches could not be read first (rule 15),
 *  then lines awaiting a decision, oldest first - by the earliest batch received, else the due date -
 *  then everything else in the API's order. Stable, and a header click still re-sorts. */
export function registerOrder<R extends Pick<RegisterRow, 'state' | 'batches' | 'expectedDate'>>(rows: readonly R[]): R[] {
  const rank = (r: R) => (r.state === 'BATCHES_UNKNOWN' ? 0 : r.state === 'AWAITING_DECISION' ? 1 : 2);
  const since = (r: R) =>
    r.batches.map((b) => b.receivedDate ?? '').filter(Boolean).sort()[0] ?? r.expectedDate ?? '9999';
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rank(a.r) - rank(b.r) || (rank(a.r) === 1 ? since(a.r).localeCompare(since(b.r)) : 0) || a.i - b.i)
    .map(({ r }) => r);
}

/** What a register row says about its batches, in a few words: the count and
 *  how many an approval covers. Null while D365 could not be read (rule 15). */
export function batchSummary(row: Pick<RegisterRow, 'batches' | 'batchReadError' | 'received' | 'lineStatus'>): {
  text: string;
  approval: { text: string; tone: Tone } | null;
  ids: string[];
} {
  if (row.batchReadError) return { text: 'Could not be read', approval: null, ids: [] };
  const n = row.batches.length;
  if (n === 0) {
    const text = row.received ? 'Received, no batch' : row.lineStatus === 'Canceled' ? 'Cancelled' : 'Not yet';
    return { text, approval: null, ids: [] };
  }
  // Approval is counted over the register's own batches; those received before
  // the cutover were the paper system's, and are said to be on paper instead.
  const paper = row.batches.filter((b) => b.onPaper === true).length;
  const ours = n - paper;
  const approved = row.batches.filter((b) => b.approved === true).length;
  // Name the packing slip when one batch id arrived on more than one slip:
  // coverage is per batch AND slip.
  const ids = row.batches.map((b) => {
    const repeated = row.batches.filter((x) => x.batchId === b.batchId).length > 1;
    return repeated && b.packingSlipId ? `${b.batchId} / ${b.packingSlipId}` : b.batchId;
  });
  const andPaper = paper > 0 ? `, ${paper} on paper` : '';
  const approval =
    ours === 0
      ? { text: 'on paper', tone: 'quiet' as Tone }
      : approved === ours
        ? { text: `${ours === 1 ? 'approved' : 'all approved'}${andPaper}`, tone: 'ok' as Tone }
        : approved === 0
          ? { text: `${ours === 1 ? 'not approved' : 'none approved'}${andPaper}`, tone: 'bad' as Tone }
          : { text: `${approved} of ${ours} approved${andPaper}`, tone: 'warn' as Tone };
  return { text: `${n} batch${n === 1 ? '' : 'es'}`, approval, ids };
}

/** The register's Certificate cell: the state's label, said fully when the batches are already
 *  covered. (UI/UX audit, 31 Oct 2026, UX-23) PO024474 read "40 batches · all approved" beside
 *  "Awaiting decision", which looked like a contradiction: every batch IS approved, and a live
 *  certificate recorded since (or not shown to that approval) still asks for its decision (rule 18,
 *  noDecisionNeeded). The state is the server's and is not recomputed here; only its words are
 *  fuller in that one case. A line D365 could not read is never called "batches approved" (rule 15). */
export function registerStateText(row: Pick<RegisterRow, 'state' | 'batches' | 'batchReadError'>): string {
  if (row.state === 'AWAITING_DECISION' && !row.batchReadError) {
    const ours = row.batches.filter((b) => b.onPaper !== true);
    if (ours.length > 0 && ours.every((b) => b.approved === true)) return 'Batches approved; a certificate awaits decision';
  }
  return STATE_LABEL[row.state];
}

/** The dock's line picker order (UI/UX audit, 31 Oct 2026, UX-43, for Receive.tsx): lines with no dock
 *  inspection first, then the rest, each group in the order given (stable). "Not inspected" is
 *  `iir.status === 'NONE'` with no goods pending; a line whose `iir` is absent is NOT KNOWN and goes
 *  first too - an unknown is never filed as done (rule 15). Order only: nothing is hidden or refused,
 *  as a second truck on the same line is legitimate. The cell's words stay `iirRegisterWord`. */
export function notInspectedFirst<R extends Pick<RegisterRow, 'iir'>>(rows: readonly R[]): R[] {
  const inspected = (r: R) => !!r.iir && (r.iir.status !== 'NONE' || r.iir.goodsPending === true);
  return [...rows.filter((r) => !inspected(r)), ...rows.filter(inspected)];
}

/** The received quantity with its stock unit: "2100 kg". Null when the unit is not known (the
 *  sync could not read it, or the line predates migration 028), so a screen keeps today's wording
 *  rather than print a number whose unit it cannot name. */
export function receivedWithUnit(row: Pick<RegisterRow, 'receivedQty' | 'stockUnit'>): string | null {
  const unit = row.stockUnit?.trim();
  if (!unit || row.receivedQty === null) return null;
  return `${row.receivedQty} ${unit}`;
}

export function stamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * Why this decision cannot be recorded as it stands, or null — the same rules
 * the server applies (noteRequiredFor in api/src/domain/cofaRules.ts, and the
 * batches_unknown refusal), shown BEFORE the click so the person fixes it rather
 * than being refused. The server checks again regardless.
 */
/** A note once the characters that print nothing are gone — format characters (zero-width
 *  spaces, joiners) and the Hangul fillers — and '' unless a letter or a digit is left. The
 *  API's visibleText, the same test. */
export function visibleText(text: string): string {
  const t = text.replace(/[\p{Cf}\u115F\u1160\u3164\uFFA0]/gu, '').trim();
  return /[\p{L}\p{N}]/u.test(t) ? t : '';
}

export function decisionBlocker(
  decision: CofaDecision,
  coverage: Coverage | null,
  batchReadError: string | null,
  note: string,
  failedOn: number | null = null,
  signature: 'NONE' | 'UNRECORDED' | null = null,
): string | null {
  if (decision === 'APPROVED' && (batchReadError || !coverage)) {
    return 'D365 could not be read for this line’s batches on the last refresh. Refresh from D365 on the register before approving.';
  }
  if (visibleText(note)) return null;
  if (decision === 'REJECTED') return 'Say what is wrong with the certificate.';
  if (decision !== 'APPROVED' || !coverage) return null;
  // Amit, 2 Oct 2026: approving what stands on a FAIL takes a written reason; NEEDS_REVIEW does not.
  if (failedOn !== null) return `Certificate #${failedOn} failed against the supplier spec. Say why you are approving.`;
  // Q8 (Anna, 13 Oct 2026), as the server applies it.
  if (signature === 'UNRECORDED') return 'No signature or authorisation is recorded for this certificate. Say what authorises it, or ask the supplier for a signed copy.';
  if (signature === 'NONE') return 'This certificate carries no signature or authorisation at all: ask the supplier for a signed copy, or say why you are approving.';
  if (!coverage.received) {
    return 'The goods are not receipted in D365 yet, so no batch can be matched. Say why you are approving now.';
  }
  if (coverage.batches.length === 0) {
    return 'D365 records the goods as received but holds no batch for them. Say why you are approving.';
  }
  const judged = coverage.batches.filter((b) => !b.onPaper);
  if (judged.length === 0) {
    return 'Every batch on this line was received before the cutover, and this certificate names none of them. Say why you are approving.';
  }
  const loose = judged.filter((b) => b.match !== 'EXACT');
  if (loose.length > 0) {
    return `${loose.map((b) => b.batchId).join(', ')} ${loose.length === 1 ? 'is' : 'are'} not named exactly on the certificate. Say why you are approving.`;
  }
  return null;
}

/**
 * The CofA column of one inspected row, as words and a tone: which certificate the bag's lot
 * matched, and where the latest decision about the batch stands (the register's answer, rule 18).
 * A batch whose latest decision is a "no" is never shown as a plain "Match".
 */
export function iirMatchText(m: Inspection['batches'][number]['cofaMatch']): { text: string; tone: Tone } {
  const no = m.standing === 'REJECTED' || m.standing === 'NEW_COFA_REQUESTED';
  const standing = m.standing ? `; the batch: ${STANDING_TEXT[m.standing]}` : '';
  if (m.kind === 'EXACT' || m.kind === 'NEAR') {
    const lot = m.lot && m.kind !== 'EXACT' ? ` (its lot ${m.lot})` : '';
    const text = `${IIR_MATCH_LABEL[m.kind]} to certificate #${m.cofaId}${lot}${standing}`;
    return { text, tone: no ? 'bad' : m.kind === 'EXACT' && m.covered ? 'ok' : 'warn' };
  }
  return { text: `${IIR_MATCH_LABEL[m.kind]}${m.kind === 'NO_CERTIFICATE' ? '' : standing}`, tone: no ? 'bad' : IIR_MATCH_TONE[m.kind] };
}

/* ── The CofA intake job (rule 25) ──────────────────────── */

export const INTAKE_LABEL: Record<IntakeOutcome, string> = {
  RECORDED: 'Recorded automatically',
  NOT_A_CERTIFICATE: 'Not a certificate',
  UNREADABLE: 'A person reads it',
  UNMATCHED: 'No PO line found',
  WAITING_RECEIPT: 'Waiting for the goods',
  DUPLICATE: 'Already recorded',
  FAILED: 'Failed',
  DRYRUN: 'Would record (dry run)',
  SAME_FILE_SECOND_DELIVERY: 'Same file, second delivery',
  STOPPED_RETRYING: 'Stopped trying: record by hand',
  NEEDS_PERSON: 'A person decides',
  ON_PAPER: 'Goods handled on paper',
};

export const INTAKE_TONE: Record<IntakeOutcome, Tone> = {
  RECORDED: 'ok',
  NOT_A_CERTIFICATE: 'quiet',
  UNREADABLE: 'warn',
  UNMATCHED: 'warn',
  WAITING_RECEIPT: 'quiet',
  DUPLICATE: 'quiet',
  FAILED: 'bad',
  DRYRUN: 'quiet',
  SAME_FILE_SECOND_DELIVERY: 'warn',
  STOPPED_RETRYING: 'warn',
  NEEDS_PERSON: 'warn',
  ON_PAPER: 'quiet',
};

export interface IntakeRow {
  id: number;
  internetMessageId: string;
  attachmentName: string;
  subject: string | null;
  senderAddress: string | null;
  emailReceivedAt: string | null;
  outcome: IntakeOutcome;
  dryRun: boolean;
  reason: string | null;
  receiptId: number | null;
  cofaId: number | null;
  at: string | null;
  po: string | null;
  line: number | null;
}

export interface IntakeRunSummary {
  ran?: boolean;
  why?: string;
  records?: number;
  syncError?: string | null;
  stopped?: string | null;
  truncated?: boolean;
}

export interface IntakePayload {
  mode: 'off' | 'dryrun' | 'on';
  lastRun: { at: string | null; summary: IntakeRunSummary | null } | null;
  rows: IntakeRow[];
}

/** The scheduled run that should have happened last: every half hour 06:00-20:00 Brisbane (UTC+10,
 *  no daylight saving), as the timer runs. */
export function lastIntakeSlot(now: Date): Date {
  const BNE = 10 * 3_600_000;
  const local = new Date(now.getTime() + BNE);
  const minutes = local.getUTCHours() * 60 + local.getUTCMinutes();
  const day = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
  const at = (dayMs: number, m: number) => new Date(dayMs + m * 60_000 - BNE);
  if (minutes < 6 * 60) return at(day - 86_400_000, 20 * 60);
  if (minutes >= 20 * 60) return at(day, 20 * 60);
  return at(day, minutes - (minutes % 30));
}

/**
 * Rule 15 (review 8): whether the list can be read as complete. Not when the job has not run, when
 * its last run is more than two hours behind the schedule, or when that run did not run, could not
 * refresh D365, stopped early or could not list the whole window. The sentence says which.
 */
export function intakeStanding(data: IntakePayload, now: Date): { known: boolean; text: string } {
  const last = data.lastRun;
  // Review round 2, 7: off means QA@ is not being read - never "nothing waiting".
  if (data.mode === 'off') return { known: false, text: 'Not known: the intake job is off, so QA@ is not being read automatically.' };
  if (!last?.at) return { known: false, text: 'Not known: the intake job has not run.' };
  const at = new Date(last.at);
  const when = `last run ${stamp(last.at)}`;
  const sm = last.summary;
  if (at.getTime() < lastIntakeSlot(now).getTime() - 2 * 3_600_000) {
    return { known: false, text: `Not known: the ${when}, more than two hours behind its schedule.` };
  }
  if (sm?.ran === false) return { known: false, text: `Not known: the ${when} did not run${sm.why ? ` - ${sm.why}` : ''}.` };
  const gaps = [
    sm?.syncError ? `D365 could not be refreshed (${sm.syncError})` : null,
    sm?.stopped ?? null,
    sm?.truncated ? 'more mail arrived than one run lists' : null,
  ].filter(Boolean);
  if (gaps.length) return { known: false, text: `Not known: the ${when} - ${gaps.join('; ')}.` };
  return { known: true, text: `Last run ${stamp(last.at)}.` };
}

/** How a certificate's recorder reads: the intake job is never a person having looked (rule 25). */
export function recordedVia(door: string): string {
  return door === 'SYSTEM' ? 'automatically by the CofA intake job, not by a person' : door === 'GATEWAY' ? 'via Claude' : 'via the app';
}

/* ── Inspection failures (migration 020) ───────────────── */

export type FailureStatus = 'OPEN' | 'CLOSED';

/** ⚠ Mirrors CK_InspectionFailureClosure_Outcome of migration 022 (vocabulary.test.ts). */
export type ClosureOutcome = 'ACCEPTED' | 'REJECTED' | 'ACCEPTED_WITH_CONDITIONS';

export const CLOSURE_OUTCOME_LABEL: Record<ClosureOutcome, string> = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  ACCEPTED_WITH_CONDITIONS: 'Accepted with conditions',
};

/** A closure's outcome in words. Null = closed before outcomes were recorded (022): "not recorded",
 *  never a decision. Absent = an API that did not say: not known (rule 15). */
export function outcomeLabel(o: ClosureOutcome | null | undefined): string {
  if (o === undefined) return 'Outcome not known';
  return o === null ? 'Outcome not recorded' : CLOSURE_OUTCOME_LABEL[o];
}

export interface FailureClosure {
  closedByUpn: string;
  closedAt: string;
  /** Optional: an older API did not send it. */
  outcome?: ClosureOutcome | null;
  note: string;
  closedAtInspectionId: number;
}

export interface InspectionFailureRef {
  failureId: number;
  status: FailureStatus;
  closure: FailureClosure | null;
}

/** One row of GET /inspection-failures. */
export interface InspectionFailure {
  failureId: number;
  status: FailureStatus;
  latestInspectionId: number;
  corrected: boolean;
  supplier: { account: string | null; name: string | null } | null;
  dateReceived: string | null;
  /** `item` and `batches` (the D365 batch, else the lot as printed) prefill an NCR (rule 29); absent from an older API. */
  /** The PO lines. A failure on a VEHICLE record (migration 033) has no batch rows, so a server that
   *  builds this from batch rows sends none: empty = not known, never "no lines". */
  lines: Array<{ receiptId: number; po: string; line: number; itemName: string; item?: string; batches?: string[] }>;
  failedChecks: Array<{ inspectionId: number; where: string; check: string; label: string; reason: string | null }>;
  photos: Array<InspectionPhoto & { inspectionId: number }>;
  inspectedByUpn: string | null;
  /** Everyone who inspected or corrected any version of the lineage: none may close it. Absent from
   *  an older API, in which case the screen falls back to the inspector. */
  recordedBy?: string[];
  openedAt: string;
  ageDays: number;
  closure: FailureClosure | null;
  reopened: boolean;
}

export interface InspectionFailuresPayload {
  status: 'open' | 'closed' | 'all';
  count: number;
  failures: InspectionFailure[];
}

/** The sentence for a record's failure on the receipt page. Undefined = the server did not say. */
export function failureSentence(f: InspectionFailureRef | null | undefined): { text: string; tone: Tone } | null {
  if (f === undefined) return { text: 'Inspection failure: not known - the server did not say.', tone: 'quiet' };
  if (f === null) return null;
  if (f.status === 'OPEN') {
    return {
      text: f.closure
        ? `Inspection failure #${f.failureId}: OPEN again - a correction failed after it was closed by ${f.closure.closedByUpn}.`
        : `Inspection failure #${f.failureId}: OPEN, waiting for QA to close it.`,
      tone: 'bad',
    };
  }
  return {
    text: `Inspection failure #${f.failureId}: closed by ${f.closure?.closedByUpn ?? 'unknown'}${f.closure ? ` ${stamp(f.closure.closedAt)}, ${outcomeLabel(f.closure.outcome)}` : ''}${f.closure?.note ? ` - ${f.closure.note}` : ''}.`,
    tone: 'ok',
  };
}

/** Whether `upn` inspected or corrected any version of this failure's lineage, so may not close it
 *  (rule 9's shape). The server decides; the screen hides the button to spare a refusal. */
export function closesOwnFailure(f: InspectionFailure, upn: string): boolean {
  const me = upn.trim().toLowerCase();
  const recorders = f.recordedBy ?? (f.inspectedByUpn ? [f.inspectedByUpn] : []);
  return recorders.some((u) => u.trim().toLowerCase() === me);
}

/** One line of the spec a certificate was judged against (the API's SpecLine, tools/cofaRegister.ts). */
export interface SpecLine {
  specParameterId: number;
  code: string;
  name: string;
  unit: string | null;
  sequence: number;
  min: number | null;
  max: number | null;
  expectedText: string | null;
  mandatory: boolean;
}
